/**
 * Victory History Manager
 *
 * Manages saved victory runs with timestamps, medals, and completion times
 * Updated to include username and collectibles for leaderboard system
 */

import { ICONS } from '../config/icons';

export interface VictoryRun {
  id: string; // Unique identifier
  username: string; // Player name
  timestamp: number; // Unix timestamp
  completionTime: number; // Time in milliseconds
  medal: 'gold' | 'silver' | 'bronze'; // Medal type
  collectibles: number; // Collectibles found (0-18)
  date: string; // Formatted date string
}

export class VictoryHistoryManager {
  private static readonly STORAGE_KEY = 'blobo-victory-history';
  private static readonly MAX_RUNS = 10; // Keep top 10 runs

  /**
   * Save a new victory run
   */
  static saveRun(
    username: string,
    completionTime: number,
    medal: 'gold' | 'silver' | 'bronze',
    collectibles: number = 0
  ): void {
    const runs = this.loadRuns();

    const newRun: VictoryRun = {
      id: this.generateId(),
      username,
      timestamp: Date.now(),
      completionTime,
      medal,
      collectibles,
      date: new Date().toLocaleDateString(),
    };

    // Add new run and sort by completion time (fastest first)
    runs.push(newRun);
    runs.sort((a, b) => a.completionTime - b.completionTime);

    // Keep only top MAX_RUNS
    const topRuns = runs.slice(0, this.MAX_RUNS);

    // Save to localStorage
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(topRuns));
  }

  /**
   * Load all saved victory runs (with migration for legacy data)
   */
  static loadRuns(): VictoryRun[] {
    const saved = localStorage.getItem(this.STORAGE_KEY);
    if (!saved) {
      return [];
    }

    try {
      const runs = JSON.parse(saved);

      // Migrate legacy data if needed
      return runs.map((run: any) => this.migrateRun(run));
    } catch (e) {
      console.error('Failed to load victory history:', e);
      return [];
    }
  }

  /**
   * Migrate legacy run data (handle runs without username/id/collectibles)
   */
  private static migrateRun(run: any): VictoryRun {
    return {
      id: run.id || this.generateId(),
      username: run.username || 'Unknown',
      timestamp: run.timestamp || Date.now(),
      completionTime: run.completionTime,
      medal: run.medal,
      collectibles: run.collectibles || 0,
      date: run.date || new Date().toLocaleDateString(),
    };
  }

  /**
   * Generate simple unique ID
   */
  private static generateId(): string {
    return `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
  }

  /**
   * Get the best (fastest) run
   */
  static getBestRun(): VictoryRun | null {
    const runs = this.loadRuns();
    return runs.length > 0 ? (runs[0] ?? null) : null;
  }

  /**
   * Format time for display
   */
  static formatTime(milliseconds: number): string {
    const minutes = Math.floor(milliseconds / 60000);
    const seconds = Math.floor((milliseconds % 60000) / 1000);
    const ms = Math.floor((milliseconds % 1000) / 100);
    return `${minutes}:${seconds.toString().padStart(2, '0')}.${ms}`;
  }

  /**
   * Get medal icon (Material Icon)
   * @param _medal - Medal type (unused, all medals use same icon with different colors)
   */
  static getMedalEmoji(_medal: 'gold' | 'silver' | 'bronze'): string {
    return ICONS.MEDAL;
  }

  /**
   * Update username for all runs (when player changes their name)
   */
  static updateUsername(oldUsername: string, newUsername: string): void {
    const runs = this.loadRuns();
    let updated = false;

    runs.forEach(run => {
      if (run.username === oldUsername) {
        run.username = newUsername;
        updated = true;
      }
    });

    if (updated) {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(runs));
    }
  }

  /**
   * Clear all history (for testing)
   */
  static clearHistory(): void {
    localStorage.removeItem(this.STORAGE_KEY);
  }

  /**
   * Add sample victory entries for testing (dev mode only)
   */
  static addSampleEntries(count: 1 | 2 | 5): void {
    // Clear existing entries first
    this.clearHistory();

    // Sample data with different medals and collectibles
    const samples: Array<{
      time: number;
      medal: 'gold' | 'silver' | 'bronze';
      collectibles: number;
    }> = [
      { time: 4 * 60 * 1000 + 32 * 1000, medal: 'gold', collectibles: 42 }, // 4:32 - gold
      { time: 6 * 60 * 1000 + 15 * 1000, medal: 'silver', collectibles: 38 }, // 6:15 - silver
      { time: 5 * 60 * 1000 + 58 * 1000, medal: 'silver', collectibles: 35 }, // 5:58 - silver
      { time: 9 * 60 * 1000 + 23 * 1000, medal: 'bronze', collectibles: 28 }, // 9:23 - bronze
      { time: 11 * 60 * 1000 + 45 * 1000, medal: 'bronze', collectibles: 31 }, // 11:45 - bronze
    ];

    // Add the requested number of sample entries
    for (let i = 0; i < count; i++) {
      const sample = samples[i];
      if (sample) {
        this.saveRun('TestPlayer', sample.time, sample.medal, sample.collectibles);
      }
    }
  }
}
